/**
 * HandleRemoteConfigUpdateUseCase
 *
 * Dispatches one Remote Config push: for every key the server says changed, run
 * whatever that key means. Port of the body of Flutter's real-time listener
 * (lib/services/remote_config_service.dart:191-322).
 *
 * Three kinds of key matter (everything else is a no-op, exactly as in Flutter's
 * `default: break;` at lib/services/remote_config_service.dart:316-319 — a bumped
 * `contentMaxSeconds` triggers nothing, its new value is simply read by the next
 * caller of the synchronous getter):
 * 1. `budgetExceeded` — re-read the flag and, only when it is now `true`, ask the
 *    UI for the budget-exceeded overlay (Flutter `_handleBudgetExceeded()`,
 *    lib/services/remote_config_service.dart:209-213, :331-340).
 * 2. The five sync-invalidation keys — `versionGifts` (:214-218),
 *    `versionRechargeWalletProducts` (:219-223), `versionPremiumPackages`
 *    (:224-228), `versionDonationProducts` (:311-315) re-sync their encrypted
 *    cache; `versionDefaultSettings` (:229-233) runs the settings rewrite.
 * 3. The seven legal-document keys (:234-310) — collected and returned so the
 *    presentation layer can show one "document updated" modal each. Note the
 *    asymmetric pair: the RC key is `versionPremiumSubscriptions` but the
 *    document is `premiumSubscriptionsPolicy` (:278-288).
 *
 * The activate() that precedes the dispatch (lib/services/remote_config_service.dart:193)
 * is owned by `remoteConfigDataService.subscribeToConfigUpdates`, so by the time
 * `execute` runs the new values are already readable through `remoteConfigService`.
 *
 * Domain vs. UI: the cache invalidations are domain work and happen here; the two
 * user-visible outcomes are *returned* as `RemoteConfigUpdateEffects` for the
 * app-level Remote Config listener to render. Flutter does them inline from the
 * service (`AppOverlayNotifier`, `NavigationService.showInfoWidgetDialog`); on the
 * web the domain layer must not reach into the UI.
 *
 * Unknown keys — both clients now tolerate them (Flutter fixed on this branch,
 * authorised by the task prompt): Flutter used to resolve the key with
 * `RemoteConfigs.values.byName(updatedConfig)` before the switch, which threw
 * `ArgumentError` on a key the client enum does not know and aborted the whole
 * `for` loop, silently dropping every remaining key of the same push; it now
 * resolves with `firstWhereOrNull` + `continue` immediately before that switch
 * (lib/services/remote_config_service.dart:202-207). Since the console template
 * is edited independently of app releases (and the web deliberately carries six
 * web-only keys Flutter's enum does not have, `domain/enums/remoteConfigs.ts`),
 * an unknown key is expected, not exceptional. The web switches on the **raw
 * string** and falls through to `default: break`, so an unknown key is ignored
 * and its siblings still process.
 *
 * DELIBERATE DIVERGENCE — per-key error containment (still live; the Flutter
 * source is NOT patched here and a follow-up Flutter fix is recommended):
 * Flutter's update loop (lib/services/remote_config_service.dart:195-321)
 * carries no try/catch, neither around the loop nor inside any arm, so a throw
 * from one arm (e.g. `await SyncRepository().syncGifts()`, :216) escapes the
 * loop and loses the keys ordered after it in that push. The web wraps each
 * key's side effect in its own try/catch (`execute`, :94-103): one failing
 * invalidation must not drop the rest of the push, and `execute` never rejects
 * — the listener that calls it is a fire-and-forget callback with nowhere to
 * surface a rejection.
 */

import type { RemoteConfigUpdateEffects } from '@/domain/entities/remoteConfig/remoteConfigUpdateEffects';
import { RemoteConfigs } from '@/domain/enums/remoteConfigs';
import { SyncEntityType } from '@/domain/enums/syncEntityType';
import { VersionUpdatedLegalDocument } from '@/domain/enums/versionUpdatedLegalDocument';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { SyncRemoteConfigSettingsChangesUseCase } from '@/domain/usecases/remoteConfig/syncRemoteConfigSettingsChangesUseCase';
import { SyncSecureStorageUseCase } from '@/domain/usecases/sync/syncSecureStorageUseCase';

export class HandleRemoteConfigUpdateUseCase {
  private syncSecureStorage: SyncSecureStorageUseCase;
  private syncRemoteConfigSettings: SyncRemoteConfigSettingsChangesUseCase;

  constructor(
    syncSecureStorage?: SyncSecureStorageUseCase,
    syncRemoteConfigSettings?: SyncRemoteConfigSettingsChangesUseCase
  ) {
    this.syncSecureStorage = syncSecureStorage || new SyncSecureStorageUseCase();
    this.syncRemoteConfigSettings =
      syncRemoteConfigSettings || new SyncRemoteConfigSettingsChangesUseCase();
  }

  /**
   * Handles every key of one push, in the order the server listed them
   * (Flutter's `for (var updatedConfig in event.updatedKeys)`,
   * lib/services/remote_config_service.dart:195), and returns the UI-facing
   * effects. Never rejects.
   */
  async execute(updatedKeys: string[]): Promise<RemoteConfigUpdateEffects> {
    const effects: RemoteConfigUpdateEffects = {
      shouldDisplayBudgetExceededOverlay: false,
      updatedLegalDocuments: [],
    };

    for (const key of updatedKeys) {
      try {
        await this.handleKey(key, effects);
      } catch (error) {
        // Containment: a failure on one key must not drop the rest of the push.
        appLogger.error(
          `Failed to handle updated Remote Config key: ${key}`,
          'HandleRemoteConfigUpdateUseCase.execute',
          { error: error as Error }
        );
      }
    }

    return effects;
  }

  /**
   * Flutter's switch (lib/services/remote_config_service.dart:208-319), over the
   * raw key string rather than a resolved enum — see the unknown-keys note in the
   * file header (Flutter skips an unknown key with `firstWhereOrNull` + `continue`
   * before the switch).
   */
  private async handleKey(key: string, effects: RemoteConfigUpdateEffects): Promise<void> {
    switch (key) {
      // ── Budget ────────────────────────────────────────────────────────────
      case RemoteConfigs.budgetExceeded:
        // Flutter `_handleBudgetExceeded()` re-reads the flag and only raises
        // the overlay when it is `true` (:331-339); clearing it shows nothing.
        if (remoteConfigService.budgetExceeded) {
          effects.shouldDisplayBudgetExceededOverlay = true;
        }
        break;

      // ── Synced-cache invalidation ─────────────────────────────────────────
      case RemoteConfigs.versionGifts:
        // Flutter: `SyncRepository().syncGifts()` (:214-218).
        await this.syncSecureStorage.resyncEntity(SyncEntityType.GIFTS);
        break;

      case RemoteConfigs.versionRechargeWalletProducts:
        // Flutter: `SyncRepository().syncRechargeWalletProducts()` (:219-223).
        await this.syncSecureStorage.resyncEntity(SyncEntityType.RECHARGE_WALLET_PRODUCTS);
        break;

      case RemoteConfigs.versionPremiumPackages:
        // Flutter: `SyncRepository().syncPremiumPackages()` (:224-228).
        await this.syncSecureStorage.resyncEntity(SyncEntityType.PREMIUM_PACKAGES);
        break;

      case RemoteConfigs.versionDonationProducts:
        // Flutter: `SyncRepository().syncDonationProducts()` (:311-315).
        await this.syncSecureStorage.resyncEntity(SyncEntityType.DONATION_PRODUCTS);
        break;

      case RemoteConfigs.versionDefaultSettings:
        // Flutter: `SyncRepository().syncRemoteConfigSettingsChanges()` (:229-233).
        await this.syncRemoteConfigSettings.execute();
        break;

      // ── Legal documents ───────────────────────────────────────────────────
      case RemoteConfigs.versionTermsOfService:
        // Flutter: (:234-244).
        effects.updatedLegalDocuments.push(VersionUpdatedLegalDocument.termsOfService);
        break;

      case RemoteConfigs.versionCoinsPolicy:
        // Flutter: (:245-255).
        effects.updatedLegalDocuments.push(VersionUpdatedLegalDocument.coinsPolicy);
        break;

      case RemoteConfigs.versionCommunityGuidelines:
        // Flutter: (:256-266).
        effects.updatedLegalDocuments.push(VersionUpdatedLegalDocument.communityGuidelines);
        break;

      case RemoteConfigs.versionExclusiveSubscriptionsPolicy:
        // Flutter: (:267-277).
        effects.updatedLegalDocuments.push(
          VersionUpdatedLegalDocument.exclusiveSubscriptionsPolicy
        );
        break;

      case RemoteConfigs.versionPremiumSubscriptions:
        // Flutter: (:278-288). Asymmetric on purpose — the key is
        // `versionPremiumSubscriptions`, the document `premiumSubscriptionsPolicy`.
        effects.updatedLegalDocuments.push(VersionUpdatedLegalDocument.premiumSubscriptionsPolicy);
        break;

      case RemoteConfigs.versionPrivacyPolicy:
        // Flutter: (:289-299).
        effects.updatedLegalDocuments.push(VersionUpdatedLegalDocument.privacyPolicy);
        break;

      case RemoteConfigs.versionRewardsPolicy:
        // Flutter: (:300-310).
        effects.updatedLegalDocuments.push(VersionUpdatedLegalDocument.rewardsPolicy);
        break;

      // ── Everything else ───────────────────────────────────────────────────
      default:
        // Flutter: `default: break;` (:316-319). Every value key, and every key
        // this client does not know, is a silent no-op.
        break;
    }
  }
}

export const handleRemoteConfigUpdateUseCase = new HandleRemoteConfigUpdateUseCase();
